import React, { ChangeEvent, InvalidEvent, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import { Modal } from 'antd';
import { deleteWithdrawAPI, getMeAPI, putMeAPI } from 'apis/auth/authAPI';
import { formatCompanyPhoneNumber, isValidatePassword } from 'constants/regularExpression';
import useUserStore from 'store/useUserStore';
import { TMeResponse, TResponseMeData } from 'types/api';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'constants/url';
import { useAuth } from 'contexts/AuthContext';
import SnackbarAlert from '../SnackbarAlert';
import { useSnackbarStore } from '../../store/useSnackbarStore';
import { removeTokensFromCookies } from '../../lib/utils/setTokensInCookies';
import useAuthStore from '../../store/useAuthStore';
import { useTranslation } from 'react-i18next';
// import { truncate } from 'node:fs/promises';

export default function EditUserInfo() {
  const { setIsLoggedIn } = useAuthStore();
  const { user, setUser } = useUserStore();
  // 유저 이름
  const [name, setName] = useState<string>(user?.name || '');
  // 회사정보
  const [companyName, setCompanyName] = useState<string>(user?.companyName || '');
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState<string>(user?.companyPhoneNumber || '');
  // 생년월일
  const [birth, setBirth] = useState<string>(user?.birth || '');
  // 비밀번호 및 상태 관리
  const [currentPassword, setCurrentPassword] = useState(''); // 현재 비밀번호
  const [newPassword, setNewPassword] = useState(''); //새 비밀번호
  const [confirmPassword, setConfirmPassword] = useState(''); //새 비밀번호 확인용
  const [curPasswordAlert, setCurrentPasswordAlert] = useState(true); // 현재 비밀번호 유효성 상태
  const [newPasswordAlert, setNewPasswordAlert] = useState(true); // 새 비밀번호 유효성 상태
  const [confirmPasswordAlert, setConfirmPasswordAlert] = useState(true); // 비밀번호 일치 여부
  const [passwordTouched, setPasswordTouched] = useState(false); // 비밀번호 확인 필드가 포커스를 벗어났는지 여부
  const navigate = useNavigate();
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar); //Alert zustand 상태 관리
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      currentPassword: formData.get('currentPassword')?.toString() || '',
      newPassword: formData.get('newPassword')?.toString() || '',
      name: formData.get('name')?.toString() || '',
      birth: `${formData.get('birth')}`,
      companyName: formData.get('companyName')?.toString() || '',
      companyPhoneNumber: formData.get('companyPhoneNumber')?.toString() || ''
    };

    try {
      const response = await putMeAPI(data);
      if (response && response.code === 200) {
        showSnackbar(t('edit_user_info.message.change_success'), 'success'); // 변경 되었음을 표시하는 모달

        // put변경이 완료되면, getMeAPI불러서 새로 데이터 업데이트!!
        const userResponse: TMeResponse = await getMeAPI();
        const userData: TResponseMeData = userResponse.data;
        setUser(userData);
        setCurrentPassword(''); // 현재 비밀번호 초기화
        setNewPassword(''); // 새 비밀번호 초기화
        setConfirmPassword(''); // 새 비밀번호 확인칸 초기화
      }
    } catch (error) {
      console.error('API 호출 중 오류 발생:', error);
      const { message } = (error as { data: any }).data as { message: string };
      showSnackbar(t('edit_user_info.message.error'), 'error');
    }
  };

  // 생년월일
  const handleDateChange = (newDate: Dayjs | null) => {
    setBirth(newDate ? newDate.format('YYYY-MM-DD') : '');
  };

  // 현재 비밀번호 입력 시 상태 업데이트 및 유효성 검사
  const handleCurrentPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputPassword = event.target.value;
    setCurrentPassword(inputPassword);
    setCurrentPasswordAlert(isValidatePassword(inputPassword)); // 유효성 검사 실행
  };

  // 새 비밀번호 입력 시 상태 업데이트 및 유효성 검사
  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputPassword = event.target.value;
    setNewPassword(inputPassword);
    setNewPasswordAlert(isValidatePassword(inputPassword)); // 유효성 검사 실행
  };
  // 새 비밀번호 확인 필드에서 포커스를 벗어날 때 호출 (비밀번호 일치여부)
  const handleConfirmPasswordBlur = () => {
    setPasswordTouched(true); // 사용자가 필드를 벗어났음을 표시
    setConfirmPasswordAlert(newPassword === confirmPassword); // 비밀번호가 일치하는지 확인
  };

  // 이름 입력시 상태 업데이트
  const handleUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputUsername = event.target.value;
    setName(inputUsername);
    // setNewPasswordAlert(isValidatePassword(inputPassword)); // 유효성 검사 실행
  };

  // 회사이름 입력시 상태 업데이트
  const handleCompanyName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputCompanyName = event.target.value;
    setCompanyName(inputCompanyName);
    // setNewPasswordAlert(isValidatePassword(inputPassword)); // 유효성 검사 실행
  };
  const handleCompanyPhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputCompanyPhoneNumber = event.target.value;
    const formattedCompanyPhoneNumber = formatCompanyPhoneNumber(inputCompanyPhoneNumber);

    // 포맷팅된 값을 상태에 저장
    setCompanyPhoneNumber(formattedCompanyPhoneNumber);
  };

  const handleDeleteAccount = () => {
    Modal.confirm({
      title: t('edit_user_info.delete_account.check_title'),
      content: t('edit_user_info.delete_account.check_content'),
      okText: t('edit_user_info.delete_account.check_ok_text'),
      cancelText: t('edit_user_info.delete_account.check_cancel_text'),
      onOk: async () => {
        try {
          const response = await deleteWithdrawAPI();

          if (response && response.code === 200) {
            // 정상적으로 응답이 오면 로그아웃 처리 및 모달 표시
            // useAuth().logout(); // 로그아웃 처리
            Modal.success({
              title: t('edit_user_info.delete_account.success_title'),
              content: t('edit_user_info.delete_account.success_content'),
              onOk: () => {
                setIsLoggedIn(false);
                removeTokensFromCookies();
                navigate('/'); // 모달 확인 후 로그인 페이지로 이동
              }
            });
          } else {
            // 응답이 200이 아니거나 실패한 경우
            showSnackbar(t('edit_user_info.delete_account.err_content1'), 'error');
          }
        } catch (error) {
          // API 호출 중 오류 발생 시 처리
          console.error('deleteWithdrawAPI 호출 에러:', error);
          showSnackbar(t('edit_user_info.delete_account.err_content2'), 'error');
        }
      }
    });
  };

  // 입력값이 유효하지 않을 경우 호출되는 함수 (커스텀 에러 메시지 설정)
  const handleInvalid = (e: InvalidEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(t('validation.required')); // 다국어 에러 메시지 설정
  };

  // 사용자가 입력을 시작하면 호출되는 함수 (에러 메시지 제거 함수)
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(''); // 에러 메시지를 초기화
  };

  // user가 업데이트되면 year, month, day, username, companyName 등의 상태를 다시 초기화
  // useEffect를 통해 user 데이터가 변경될 때 상태를 다시 설정
  useEffect(() => {
    if (user) {
      setName(user.name || ''); // user.username이 undefined일 경우 빈 문자열로 설정
      setCompanyName(user.companyName || '');
      setCompanyPhoneNumber(user.companyPhoneNumber || '');
      setBirth(user.birth || '');
    }
  }, [user]);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localStorage.getItem('language') || 'ko'}>
      <SnackbarAlert />
      <Box padding={2} display="flex" flexDirection="column" gap={2} width="100%" maxWidth={600} margin="auto">
        {/* Main */}
        <form onSubmit={handleSubmit}>
          <Box borderRadius={1} bgcolor={theme.palette.contentBlock.background} padding={2} display="flex" flexDirection="column" gap={2}>
            {/* 내 정보 */}
            <Typography variant="h5" fontWeight="bold">
              {t('edit_user_info.title')}
            </Typography>

            <Box borderRadius={1} bgcolor={theme.palette.contentBlock.main} boxShadow={1} padding={2} display="flex" flexDirection="column" gap={2}>
              {/* 내/외국인 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={32}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.is_native_foreigners')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {user?.type === 'NATIVE' ? t('edit_user_info.native') : t('edit_user_info.foreigner')}
                </Typography>
              </Box>
              {/* 아이디 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={32}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.id')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {user?.username}
                </Typography>
              </Box>
              {/* 현재 비밀번호 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.cur_pw')}
                  <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                </Typography>
                <TextField
                  required
                  onInvalid={handleInvalid} // 유효하지 않을 때 호출
                  onInput={handleInput} // 입력 시 호출
                  name="currentPassword"
                  type="password"
                  size="small"
                  placeholder={t('edit_user_info.message.cur_pw')}
                  value={currentPassword}
                  onChange={handleCurrentPasswordChange}
                  onBlur={() => setPasswordTouched(true)}
                  // error={passwordTouched && !curPasswordAlert} // 필드가 떠난 후에만 에러 표시
                  // helperText={passwordTouched && !curPasswordAlert ? '영문, 숫자, 특수문자를 각 1개씩 포함하여 8~32자리이어야 합니다.' : null}
                  sx={{ flexGrow: 1, maxWidth: 230 }}
                />
              </Box>
              {/* 새 비밀번호 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.new_pw')}
                </Typography>
                <TextField
                  name="newPassword"
                  type="password"
                  size="small"
                  placeholder={t('edit_user_info.message.new_pw')}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  onBlur={() => setPasswordTouched(true)}
                  error={passwordTouched && !newPasswordAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={passwordTouched && !newPasswordAlert ? t('edit_user_info.message.pw_validity') : null}
                  sx={{ flexGrow: 1, maxWidth: 230 }}
                />
              </Box>
              {/* 새 비밀번호 확인 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.new_pw_check')}
                </Typography>
                <TextField
                  type="password"
                  size="small"
                  placeholder={t('edit_user_info.message.new_pw_check')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={handleConfirmPasswordBlur} // 포커스를 벗어날 때 유효성 검사 실행
                  error={passwordTouched && !confirmPasswordAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={passwordTouched && !confirmPasswordAlert ? t('edit_user_info.message.pw_wrong') : null}
                  sx={{ flexGrow: 1, maxWidth: 230 }}
                />
              </Box>
              {/* 이름 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={32}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.name')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {user?.name}
                </Typography>
              </Box>
              {/* 생년월일 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={32}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.birth')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {user?.birth}
                </Typography>
              </Box>
              {/* 이메일 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={32}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.email')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {user?.email}
                </Typography>
              </Box>
              {/* 휴대전화 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={32}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.phone')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {user?.phoneNumber}
                </Typography>
              </Box>
              {/* 회사이름 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.company_name')}
                  <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                </Typography>
                <TextField
                  required
                  onInvalid={handleInvalid} // 유효하지 않을 때 호출
                  onInput={handleInput} // 입력 시 호출
                  name="companyName"
                  size="small"
                  placeholder={t('edit_user_info.message.company_name')}
                  value={companyName}
                  onChange={handleCompanyName}
                  sx={{ flexGrow: 1, maxWidth: 230 }}
                />
              </Box>
              {/* 회사연락처 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={130} variant="caption" color={theme.palette.fieldText.title}>
                  {t('edit_user_info.company_phone')}
                  <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                </Typography>
                <TextField
                  required
                  onInvalid={handleInvalid} // 유효하지 않을 때 호출
                  onInput={handleInput} // 입력 시 호출
                  name="companyPhoneNumber"
                  size="small"
                  placeholder={t('edit_user_info.message.company_phone')}
                  value={companyPhoneNumber}
                  onChange={handleCompanyPhoneNumber}
                  sx={{ flexGrow: 1, maxWidth: 230 }}
                />
              </Box>
            </Box>
          </Box>

          {/* 변경 및 회원탈퇴 버튼 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '15px'
            }}
          >
            {/* 변경 */}
            <Button fullWidth variant="contained" color="primary" sx={{ width: '90px', borderRadius: '4px', fontWeight: 'bold' }} type="submit">
              {t('edit_user_info.change_btn')}
            </Button>
            {/* 회원탈퇴 */}
            <Button variant="outlined" color="error" sx={{ width: '90px', borderRadius: '4px', fontWeight: 'bold' }} onClick={handleDeleteAccount}>
              {t('edit_user_info.delete_account_btn')}
            </Button>
          </Box>
        </form>
      </Box>
    </LocalizationProvider>
  );
}
